export type SceneToolId = "describe" | "expand" | "brainstorm" | "rewrite_tone";

export type SceneTool = {
  id: SceneToolId;
  label: string;
  shortPrompt: string;
};

export const SCENE_TOOLS: SceneTool[] = [
  {
    id: "describe",
    label: "Describe",
    shortPrompt: "Enrich this passage with sensory detail",
  },
  {
    id: "expand",
    label: "Expand",
    shortPrompt: "Lengthen this thin scene while keeping voice",
  },
  {
    id: "brainstorm",
    label: "Brainstorm",
    shortPrompt: "Suggest five twists or beats for this chapter",
  },
  {
    id: "rewrite_tone",
    label: "Rewrite tone",
    shortPrompt: "Rewrite this selection in a clearer, tighter tone",
  },
];

const SHARED_CONSTRAINTS = [
  "Do not invent new plot facts that contradict the excerpt.",
  "Preserve character names exactly as they appear.",
].join(" ");

function appendStoryCraftContext(
  base: string,
  args: {
    storyCraftChecklist?: string[];
    storyCraftSummary?: string;
  }
): string {
  const summary = args.storyCraftSummary?.trim() || "";
  const bullets = (args.storyCraftChecklist ?? [])
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 5);
  if (!summary && bullets.length === 0) return base;

  const lines = [
    base,
    "",
    "Story Craft context (address if relevant, do not invent plot):",
  ];
  if (summary) lines.push(summary);
  for (const bullet of bullets) {
    lines.push(`- ${bullet}`);
  }
  return lines.join("\n");
}

export function buildSceneToolPrompt(
  tool: SceneToolId,
  args: {
    selection?: string;
    chapterTitle: string;
    chapterExcerpt: string;
    storyCraftChecklist?: string[];
    storyCraftSummary?: string;
  }
): string {
  const selection = args.selection?.trim() || "";
  const chapterTitle = args.chapterTitle.trim() || "Untitled chapter";
  const excerpt = args.chapterExcerpt.trim();
  const focus = selection || excerpt;
  const focusLabel = selection ? "selection" : "passage";

  let base: string;
  switch (tool) {
    case "describe":
      base = [
        `Chapter: "${chapterTitle}"`,
        `Enrich the following ${focusLabel} with sensory detail (sight, sound, smell, touch, taste) while keeping the author's voice.`,
        "Output prose only — no headings, notes, or meta commentary.",
        SHARED_CONSTRAINTS,
        "",
        focus,
      ].join("\n");
      break;
    case "expand":
      base = [
        `Chapter: "${chapterTitle}"`,
        `Lengthen the following thin ${focusLabel} into a fuller scene. Keep the same voice, POV, and pacing feel.`,
        "Output prose only — no headings, notes, or meta commentary.",
        SHARED_CONSTRAINTS,
        "",
        focus,
      ].join("\n");
      break;
    case "brainstorm":
      base = [
        `Chapter: "${chapterTitle}"`,
        "Suggest 5 numbered twists or story beats that could deepen this chapter.",
        "Output numbered beats only (1–5) — no prose rewrite, headings, or meta commentary.",
        SHARED_CONSTRAINTS,
        "",
        excerpt || focus,
      ].join("\n");
      break;
    case "rewrite_tone":
      base = [
        `Chapter: "${chapterTitle}"`,
        `Rewrite the following ${focusLabel} in a clearer, tighter tone. Keep meaning and character voice; cut fluff.`,
        "Output prose only — no headings, notes, or meta commentary.",
        SHARED_CONSTRAINTS,
        "",
        focus,
      ].join("\n");
      break;
    default: {
      const _exhaustive: never = tool;
      return _exhaustive;
    }
  }

  return appendStoryCraftContext(base, args);
}
